import { Box, Text, Stack, Button, Heading } from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import List from '../../../shared/List'
import { EditBalanceForm } from './EditBalanceForm'
import { useIntl, defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { formatCurrency } from '../../../../helpers/formatters'
import { useTrackingContext } from '../../../../providers/tracking'
import { addLoan, cancelEditBalance } from '../../../../actions/loan'
import { TrackingEventConstants } from '../../../../constants/tracking'
import { LoanUpdateWarning } from '../LoanUpdateWarning/LoanUpdateWarning'
import { selectEditableLoanBalance, selectEditableLoanEstimatedBalance } from '../../../../store/selectors/home/loans'
import { HOMEOWNER_DIGEST_UPDATE_LOAN_BALANCE_MODULE } from '../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'

export const MSG = defineMessages({
  infoHead: {
    id: 'Mortgages.editBalance.infoHeading',
    defaultMessage: 'Made extra payments?'
  },
  formHead: {
    id: 'Mortgages.editBalance.heading',
    defaultMessage: 'Update your loan balance'
  },
  infoTxt: {
    id: 'Mortgages.editBalance.infoExplainer',
    defaultMessage:
      "If you've made extra principal payments or have had your balance adjusted, it's good to true-up your balance here."
  },
  infoTxt2: {
    id: 'Mortgages.editBalance.autoPilot',
    defaultMessage: "Otherwise, you can stay on auto-pilot and we'll keep you up to date each month!"
  },
  formTxt: {
    id: 'Mortgages.editBalance.accuracy',
    defaultMessage:
      'For your Homebot to be accurate, one of the most important things you can do is have a correct loan balance. And here’s why —'
  },
  formTxt2: {
    id: 'Mortgages.editBalance.basedOn',
    defaultMessage: 'We calculate all of your numbers based on:'
  },
  bullet1: {
    id: 'Mortgages.editBalance.bulletOne',
    defaultMessage: 'Loan details'
  },
  bullet2: {
    id: 'Mortgages.editBalance.bulletTwo',
    defaultMessage: "Today's balance"
  },
  bullet3: {
    id: 'Mortgages.editBalance.bulletThree',
    defaultMessage: 'Extra monthly principal payments'
  },
  value: {
    id: 'Mortgages.editBalance.estimatedValueHeading',
    defaultMessage: 'Your estimated balance'
  },
  change: {
    id: 'Mortgages.editBalance.infoChangeButton',
    defaultMessage: 'Change balance'
  },
  skip: {
    id: 'Mortgages.editBalance.infoSkipButton',
    defaultMessage: 'Skip for now'
  },
  warningHead: {
    id: 'Mortgages.cannotIncreaseBalance.head',
    defaultMessage: 'Loan balance cannot be increased'
  },
  warningTxt: {
    id: 'Mortgages.cannotIncreaseBalance.text',
    defaultMessage: 'Add a new loan if you need to increase your balance'
  },
  warningBtn: {
    id: 'Mortgages.cannotIncreaseBalance.addLoan',
    defaultMessage: 'Add new loan'
  }
})

export const EditBalance = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { trackingClient } = useTrackingContext()
  const userEnteredBalance = useSelector(selectEditableLoanBalance)
  const [showForm, setShowForm] = useState(!userEnteredBalance)
  const estimatedPrincipalLeft = useSelector(selectEditableLoanEstimatedBalance)
  const [showCannotIncreaseBalanceMsg, setShowCannotIncreaseBalanceMsg] = useState(false)

  const handleAddLoan = () => {
    trackingClient?.clientCustomized({
      customization_type: TrackingEventConstants.CLIENT_ADDED_LOAN
    })

    dispatch(cancelEditBalance())
    dispatch(addLoan())
  }

  return (
    <>
      <Heading mb={6} textAlign='left' fontWeight='semibold'>
        {intl.formatMessage(showForm ? MSG.formHead : MSG.infoHead)}
      </Heading>
      <Text size='sm' marginY='4'>
        {intl.formatMessage(showForm ? MSG.formTxt : MSG.infoTxt)}
      </Text>
      <Text as='span' size='sm' marginBottom='4'>
        {intl.formatMessage(showForm ? MSG.formTxt2 : MSG.infoTxt2)}
        {showForm && (
          <List.Bulleted>
            {[MSG.bullet1, MSG.bullet2, MSG.bullet3].map(msg => (
              <Text key={msg.defaultMessage} size='sm' textAlign='left'>
                {intl.formatMessage(msg)}
              </Text>
            ))}
          </List.Bulleted>
        )}
      </Text>
      {showCannotIncreaseBalanceMsg && (
        <LoanUpdateWarning
          btnTxt={MSG.warningBtn}
          onClick={handleAddLoan}
          bodyTxt={MSG.warningTxt}
          headerTxt={MSG.warningHead}
        />
      )}
      <Box
        display='flex'
        gap='6'
        borderRadius='.25rem'
        paddingX='6'
        paddingY='4'
        marginTop='8'
        justifyContent='space-between'
        // @ts-ignore
        backgroundColor='var(--brand-surface-layer-50)'
        flexDirection={showForm ? 'column' : ['column', 'row']}
      >
        <Box>
          <Heading as='h5' size='xs' textAlign='left' variant='eyebrow' color='neutral-700'>
            {intl.formatMessage(MSG.value)}
          </Heading>
          <Heading as='h4' size='lg' color='success' textAlign='left' fontWeight='semibold'>
            {formatCurrency(
              Math.floor(
                userEnteredBalance && userEnteredBalance < estimatedPrincipalLeft
                  ? userEnteredBalance
                  : estimatedPrincipalLeft
              )
            )}
          </Heading>
        </Box>
        <Box borderTop={showForm ? '1px solid var(--brand-primary-border-50)' : ''}>
          {showForm ? (
            <EditBalanceForm
              showCannotIncreaseBalanceMsg={showCannotIncreaseBalanceMsg}
              setShowCannotIncreaseBalanceMsg={setShowCannotIncreaseBalanceMsg}
            />
          ) : (
            <Stack width='100%'>
              <Button
                size='sm'
                colorScheme='primary'
                width={['auto', '100%']}
                onClick={() => setShowForm(true)}
                data-qa='EditBalanceInterstitialNextButton'
                tracking={{
                  guid: 'h6Pwz7VDTTJGtSB7TDqS3D',
                  ui_context: HOMEOWNER_DIGEST_UPDATE_LOAN_BALANCE_MODULE,
                  descriptor: MSG.change.defaultMessage
                }}
              >
                {intl.formatMessage(MSG.change)}
              </Button>
              <Button
                size='sm'
                width={['auto', '100%']}
                onClick={() => dispatch(cancelEditBalance())}
                data-qa='EditBalanceInterstitialCancelButton'
                tracking={{
                  guid: '6EPTmM55z9b4ihX8ApJh1Y',
                  ui_context: HOMEOWNER_DIGEST_UPDATE_LOAN_BALANCE_MODULE,
                  descriptor: MSG.skip.defaultMessage
                }}
              >
                {intl.formatMessage(MSG.skip)}
              </Button>
            </Stack>
          )}
        </Box>
      </Box>
    </>
  )
}
